import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import Landing from "./components/Landing";
import TaxCalculator from "./components/TaxCalculator";
import Affordability from "./components/Affordability";
import MonthlyFinances from "./components/MonthlyFinances";
import About from "./components/About";
import AdSenseSlot from "./components/AdSenseSlot";
import AuthModal from "./components/AuthModal";
import SavedCalculationsModal from "./components/SavedCalculationsModal";
import { useAuth } from "./components/AuthProvider";
import type { CalculationType } from "./utils/api";
import "./App.css";

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

function App() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("#home");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [pendingAfterAuthCallback, setPendingAfterAuthCallback] = useState<(() => void) | null>(null);
  const [pendingRestore, setPendingRestore] = useState<{ type: CalculationType; payload: Record<string, unknown>; calculationId?: string; name?: string; createdAt?: string } | null>(null);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // After tab changes, reset window scroll. Landing can be scrolled far down; opening a
  // calculator without resetting leaves the viewport offset and clips the top of the view.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const handleRestoreCalculation = (type: CalculationType, payload: Record<string, unknown>, calculationId?: string, name?: string, createdAt?: string) => {
    setPendingRestore({ type, payload, calculationId, name, createdAt });
      const tab =
      type === "affordability" || type === "bond"
        ? "#affordability"
        : type === "monthlyFinances"
        ? "#monthlyfinances"
        : "#tax";
    handleTabChange(tab);
  };

  // Sync modal open flag when user becomes signed in (modal is hidden via `!user` anyway).
  // Do not clear pendingAfterAuthCallback here — it must survive until AuthModal onSuccess runs save-after-login.
  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  const openAuthForSave = (callback: () => void) => {
    if (user) return;
    setPendingAfterAuthCallback(() => callback);
    setShowAuthModal(true);
  };

  const handleAuthSuccess = () => {
    if (pendingAfterAuthCallback) {
      pendingAfterAuthCallback();
      setPendingAfterAuthCallback(null);
    }
    setShowAuthModal(false);
  };

  // Track page views when tab changes
  useEffect(() => {
    const pageName =
      activeTab === "#home"
        ? "Home"
        : activeTab === "#tax"
        ? "Tax Calculator"
        : activeTab === "#affordability"
        ? "Affordability"
        : activeTab === "#monthlyfinances"
        ? "Monthly Finances"
        : "About";

    // Send page view to Google Analytics
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: pageName,
        page_location: window.location.href + activeTab,
        page_path: activeTab,
      });
    }
  }, [activeTab]);

  // OAuth callback: Amplify completes the flow on load; redirect to app home
  const isCallback = typeof window !== "undefined" && window.location.pathname === "/callback";
  useEffect(() => {
    if (!isCallback) return;
    const t = setTimeout(() => {
      window.history.replaceState({}, "", "/");
      window.location.hash = "#affordability";
    }, 1500);
    return () => clearTimeout(t);
  }, [isCallback]);

  // Allow body to scroll on the landing page; lock it on calculator pages
  useEffect(() => {
    document.body.style.overflow = activeTab === "#home" ? "auto" : "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [activeTab]);

  if (isCallback) {
    return (
      <div className="App" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <p style={{ color: "var(--color-text-muted)" }}>Completing sign in…</p>
      </div>
    );
  }

  const clearPendingRestore = () => setPendingRestore(null);

  const renderContent = () => {
    switch (activeTab) {
      case "#home":
        return (
          <Landing
            onNavigate={handleTabChange}
            onOpenAuth={() => setShowAuthModal(true)}
          />
        );
      case "#monthlyfinances":
        return (
          <MonthlyFinances
            onOpenAuthForSave={openAuthForSave}
            onOpenAuth={() => setShowAuthModal(true)}
            pendingRestore={pendingRestore}
            onClearPendingRestore={clearPendingRestore}
          />
        );
      case "#tax":
        return (
          <TaxCalculator
            onOpenAuthForSave={openAuthForSave}
            onOpenAuth={() => setShowAuthModal(true)}
            pendingRestore={pendingRestore?.type === "tax" ? pendingRestore.payload : undefined}
            pendingCalculationId={pendingRestore?.type === "tax" ? pendingRestore.calculationId : undefined}
            pendingName={pendingRestore?.type === "tax" ? pendingRestore.name : undefined}
            pendingCreatedAt={pendingRestore?.type === "tax" ? pendingRestore.createdAt : undefined}
            onClearPendingRestore={clearPendingRestore}
          />
        );
      case "#about":
        return <About />;
      case "#affordability":
      default:
        return (
          <Affordability
            onOpenAuthForSave={openAuthForSave}
            onOpenAuth={() => setShowAuthModal(true)}
            pendingRestore={pendingRestore}
            onClearPendingRestore={clearPendingRestore}
          />
        );
    }
  };

  return (
    <div className="App">
      <Header
        onTabChange={handleTabChange}
        activeTab={activeTab}
        onOpenAuth={() => setShowAuthModal(true)}
        onOpenSavedModal={() => setShowSavedModal(true)}
        onRestoreCalculation={handleRestoreCalculation}
      />
      {renderContent()}
      <AdSenseSlot />
      {showAuthModal && !user && (
        <AuthModal
          onClose={() => {
            setPendingAfterAuthCallback(null);
            setShowAuthModal(false);
          }}
          onSuccess={handleAuthSuccess}
        />
      )}
      <SavedCalculationsModal
        open={showSavedModal}
        onClose={() => setShowSavedModal(false)}
        onRestoreCalculation={handleRestoreCalculation}
        onTabChange={handleTabChange}
      />
    </div>
  );
}

export default App;
